import React from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import InputField from '../components/inputFields/InputField';
import Button from '../components/buttons/Button';
import { callPincodeApi, applyPincodeToForm } from '../services/pincode';
import { searchProduct } from '../services/search';
import FormikDropdown from '../components/inputFields/FormikDropDown';
import FormikMultiAsyncDropdown from '../components/inputFields/FormikMultiAsyncDropdown';
import { sanitizeMobile } from '../utils/phone';

const defaultValues = {
    name: '',
    phone: '',
    email: '',
    shop_name: '',
    products: [],
    lead_source: 'meta_ads',
    category: 'product',
    address_line1: '',
    pincode: '',
    state: '',
    district: '',
    village: '',
    note: ''
};

const AddLeadForm = ({
    initialValues = {},
    onSubmit,
    onCancel,
    isSubmitting,
}) => {
    const validationSchema = Yup.object({
        phone: Yup.string()
            .required('Phone is required'),
        lead_source: Yup.string().required('Source is required'),
    });

    const formInitialValues = { ...defaultValues, ...initialValues }; // ✅ Merges safely


    const sourceOptions = [
        { value: 'exhibition', label: 'Exhibition' },
        { value: 'google_ads', label: 'Google Ads' },
        { value: 'meta_ads', label: 'Meta Ads' },
        { value: 'website', label: 'Website' },
        { value: 'referral', label: 'Referral' },
        { value: 'social_media', label: 'Social Media' },
        { value: 'on_ground', label: 'On Ground' },
        { value: 'other', label: 'Other' },
    ];

    const categoryOptions = [
        { value: 'product', label: 'Product Inquiry' },
        { value: 'retailer', label: 'Retailer' },
        { value: 'fpo', label: 'FPO' },
        { value: 'satellite', label: 'Satellite' },
        { value: 'other', label: 'Other' }
    ];

    return (
        <Formik
            initialValues={formInitialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
            enableReinitialize
        >
            {({ isSubmitting: formikSubmitting, setFieldValue, resetForm, values }) => (
                <Form className="space-y-3">
                    <div className="pb-2 border-b mb-4">
                        <h3 className="text-xl font-semibold text-gray-800">
                            {initialValues?.id ? 'Edit Lead' : 'Add Lead'}
                        </h3>
                    </div>

                    {/* 3-column grid */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                        <InputField label="Name" name="name" type="text" placeholder="Enter lead name" />
                        <InputField label="Phone" name="phone" type="text" placeholder="Enter phone number" required onChange={(e) => setFieldValue('phone', sanitizeMobile(e.target.value))} />
                        <InputField label="Email" name="email" type="email" placeholder="Enter email address" />
                        <InputField label="Shop/Business Name" name="shop_name" type="text" placeholder="Enter shop name" />
                        <FormikDropdown label="Lead Source" name="lead_source" options={sourceOptions} placeholder="Select source" />
                        <FormikDropdown label="Category" name="category" options={categoryOptions} placeholder="Select category" />
                    </div>

                    {/* Address */}
                    <div className="grid grid-cols-2 gap-2">
                        <FormikMultiAsyncDropdown
                            name={`products`}
                            label={"Products"}
                            apiCallFunction={async (input) => {
                                const response = await searchProduct(input);
                                return response.data.results.map((pro) => ({
                                    value: pro.id,
                                    label: pro.product_name,
                                    price: pro.price,
                                }));
                            }}
                            needSearch
                            className="w-full"
                            placeholder="Search product..."
                        />
                        <InputField label="Address" name="address_line1" type="text" placeholder="Enter address" />
                    </div>

                    {/* Pincode & Location */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                        <InputField
                            label="Pincode"
                            name="pincode"
                            type="text"
                            placeholder="Enter pincode"
                            onChange={async (value) => {
                                setFieldValue("pincode", value.target.value);
                                if (value.target.value.length === 6) {
                                    const resp = await callPincodeApi({ value: value.target.value });
                                    applyPincodeToForm(setFieldValue, resp, { cityField: "village" });
                                }
                            }}
                        />
                        <InputField label="State" name="state" type="text" placeholder="Enter state" />
                        <InputField label="District" name="district" type="text" placeholder="Enter district" />
                        <InputField label="Village" name="village" type="text" placeholder="Enter village" />
                    </div>

                    {/* Notes */}
                    <div className="grid grid-cols-1 gap-2">
                        <InputField
                            label="Notes"
                            name="note"
                            type="textarea"
                            placeholder="Enter any additional notes"
                            rows={2}
                        />
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end gap-2 pt-3">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onCancel}
                            disabled={isSubmitting || formikSubmitting}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={isSubmitting || formikSubmitting}
                        >
                            {isSubmitting || formikSubmitting ? 'Saving...' : 'Save Lead'}
                        </Button>
                    </div>
                </Form>
            )}
        </Formik>
    );
};

export default AddLeadForm;
